import React, { useCallback, useRef } from "react";

/**
 * A draggable rectangle over the photo.
 *
 * Coordinates are fractions of the image (0–1), not pixels, so the boxes hold
 * their place when the image is resized, rotated between portrait and landscape,
 * or the window changes — and so the measurement never depends on how big the
 * picture happened to be drawn.
 *
 * Pointer events rather than mouse or touch: one code path covers a mouse, a
 * finger and a stylus, and pointer capture keeps a drag alive when the finger
 * slides off the handle, which on a phone is most of them.
 */

const clamp01 = (n) => Math.min(1, Math.max(0, n));

// Corner handles alone make a box you can only resize diagonally; the edges are
// what let you nudge one border without disturbing the other three.
const HANDLES = [
  ["nw", 0, 0], ["n", 0.5, 0], ["ne", 1, 0],
  ["w", 0, 0.5], ["e", 1, 0.5],
  ["sw", 0, 1], ["s", 0.5, 1], ["se", 1, 1],
];

export default function CardBox({ box, onChange, label, variant, minSize = 0.02, onProbe }) {
  const dragRef = useRef(null);

  /* Returns null rather than a point when there is nothing sound to compute
     from — an unlaid-out stage with no width, or an event carrying no
     coordinates. Without the guard those divide into NaN, NaN is written
     straight into the box, and the browser then rejects `left: NaN%` and
     leaves the box looking untouched while every reading off it is gone. */
  const pointFrom = useCallback((e, el) => {
    const host = el.closest(".cen-stage");
    if (!host) return null;
    const r = host.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    // The stage size rides along so the magnifier can work in pixels without
    // measuring the same element a second time on every pointer move.
    return { x, y, stageW: r.width, stageH: r.height };
  }, []);

  const start = useCallback(
    (e, mode) => {
      e.preventDefault();
      e.stopPropagation();
      const from = pointFrom(e, e.currentTarget);
      if (!from) return;
      /* Capture is an improvement, not a requirement — it keeps the drag alive
         when the finger slides off the handle. It throws outright for a
         pointer id it does not recognise, and letting that escape would take
         down the drag it was meant to help. */
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {
        /* dragging still works, it just ends if you leave the element */
      }
      dragRef.current = { mode, from, box: { ...box } };
      onProbe?.(from);
    },
    [box, pointFrom, onProbe]
  );

  const move = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d) return;
      const p = pointFrom(e, e.currentTarget);
      if (!p) return;
      onProbe?.(p);
      const dx = p.x - d.from.x;
      const dy = p.y - d.from.y;
      const b = d.box;

      if (d.mode === "move") {
        onChange({
          ...b,
          x: clamp01(Math.min(b.x + dx, 1 - b.w)),
          y: clamp01(Math.min(b.y + dy, 1 - b.h)),
        });
        return;
      }

      /* Each edge moves on its own, and is stopped before it crosses the
         opposite one — a box turned inside out reads as a negative border,
         which is not a measurement of anything. */
      let { x, y, w, h } = b;
      if (d.mode.includes("w")) {
        const nx = clamp01(Math.min(b.x + dx, b.x + b.w - minSize));
        w += x - nx;
        x = nx;
      }
      if (d.mode.includes("e")) w = Math.max(minSize, Math.min(1 - x, b.w + dx));
      if (d.mode.includes("n")) {
        const ny = clamp01(Math.min(b.y + dy, b.y + b.h - minSize));
        h += y - ny;
        y = ny;
      }
      if (d.mode.includes("s")) h = Math.max(minSize, Math.min(1 - y, b.h + dy));
      onChange({ x, y, w, h });
    },
    [onChange, pointFrom, minSize, onProbe]
  );

  const end = useCallback(
    (e) => {
      dragRef.current = null;
      onProbe?.(null);
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch {
        /* nothing was captured; there is nothing to give back */
      }
    },
    [onProbe]
  );

  /* Dragging is no good without a mouse or a steady hand, and the last percent
     of a border is exactly where a grade turns. Arrows nudge the whole box;
     with shift they move its far edge, so a border can be trimmed on its own. */
  const onKeyDown = useCallback(
    (e) => {
      const step = e.altKey ? 0.001 : 0.005;
      const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      if (!d) return;
      e.preventDefault();
      const [ix, iy] = d;
      if (e.shiftKey) {
        onChange({
          ...box,
          w: Math.max(minSize, Math.min(1 - box.x, box.w + ix * step)),
          h: Math.max(minSize, Math.min(1 - box.y, box.h + iy * step)),
        });
      } else {
        onChange({
          ...box,
          x: clamp01(Math.min(box.x + ix * step, 1 - box.w)),
          y: clamp01(Math.min(box.y + iy * step, 1 - box.h)),
        });
      }
    },
    [box, onChange, minSize]
  );

  const style = {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };

  return (
    <div
      className={`cen-box cen-box--${variant}`}
      style={style}
      onPointerDown={(e) => start(e, "move")}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="group"
      aria-label={`${label} — drag to move, arrow keys to nudge, shift and arrows to resize`}
    >
      <span className="cen-box-tag">{label}</span>
      {HANDLES.map(([mode, hx, hy]) => (
        <span
          key={mode}
          className={`cen-handle cen-handle--${mode}`}
          style={{ left: `${hx * 100}%`, top: `${hy * 100}%` }}
          onPointerDown={(e) => start(e, mode)}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
      ))}
    </div>
  );
}
